import { FormikProps, FormikHelpers } from 'formik';
import { isEqual } from 'lodash';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import styled from 'styled-components/macro';
import { FloppyDiskIcon, TimesSolidIcon } from '../../../icons/icons';
import { RequestFailedPage } from '../../../infrastructure/api/RequestFailedPage';
import { useWindowTitle } from '../../../infrastructure/hooks/useWindowTitle';
import { MinimalButton } from '../../../infrastructure/interface/buttons/MinimalButton';
import { Header1 } from '../../../infrastructure/interface/components/Headers';
import { useInternationalisation } from '../../../internationalisation/hooks/useInternationalisation';
import { DesktopOnly } from '../../../styling/layout/DesktopOnly';
import { MobileOnly } from '../../../styling/layout/MobileOnly';
import { PaddedPage } from '../../../styling/layout/PaddedPage';
import { RequiresUserRole } from '../../authentication/UserRoles';
import {
  BaseCreateEditDashboardCommand,
  CreateEditDashboardForm,
  CreateEditDashboardFormModel,
  mapCreateEditDashboardFormModelToBaseCommand,
} from './CreateEditDashboardForm';
import { usePostJson } from '../../../infrastructure/api/usePostJson';
import { spacing16 } from '../../../styling/design/spacing';
import { ActionAlert } from '../../../infrastructure/interface/components/ActionAlert';
import { roleCodeCharactersByRoleName } from '../../authentication/UserRole';
import { NoMobileContentAlert } from '../../../infrastructure/interface/components/NoMobileContentAlert';

export const CreateDashboard = () => {
  const { translate } = useInternationalisation();
  useWindowTitle(translate('pages.createDashboard.title'));

  return (
    <RequiresUserRole userRole="Administrator">
      <MobileOnly>
        <MobileCreateDashboardComponent />
      </MobileOnly>
      <DesktopOnly>
        <DesktopCreateDashboardComponent />
      </DesktopOnly>
    </RequiresUserRole>
  );
};

const MobileCreateDashboardComponent = () => {
  const { translate } = useInternationalisation();

  return (
    <PaddedPage>
      <Header1>{translate('pages.createDashboard.header')}</Header1>
      <NoMobileContentAlert header={translate('pages.createDashboard.mobileAlertHeader')} />
    </PaddedPage>
  );
};

const DesktopCreateDashboardComponent = () => {
  const { translate } = useInternationalisation();
  const navigate = useNavigate();

  const [innerComponentApiError, setInnerComponentApiError] = useState<string | null>(null);
  const [navigationGuardModalIsOpen, setNavigationGuardModalIsOpen] = useState<boolean>(false);
  const formRef = useRef<FormikProps<CreateEditDashboardFormModel> | null>(null);

  const returnToDashboards = () => navigate('/dashboards');
  const closeNavigationGuardModal = () => setNavigationGuardModalIsOpen(false);

  const createDashboardRequest = usePostJson<CreateDashboardCommand, {}>(
    '/api/dashboards/CreateDashboard'
  );

  const onFormSubmit = (
    formModel: CreateEditDashboardFormModel,
    formikHelpers: FormikHelpers<CreateEditDashboardFormModel>
  ) => {
    const command: CreateDashboardCommand = mapCreateEditDashboardFormModelToBaseCommand(formModel);

    createDashboardRequest.makeRequest({
      requestBody: command,
      onSuccess: () => returnToDashboards(),
      onFailure: () => formikHelpers.setSubmitting(false),
    });
  };

  const onSaveClick = () => {
    formRef.current?.submitForm();
  };

  const onCancelClick = () => {
    if (isEqual(formRef.current?.values, initialFormModel)) {
      returnToDashboards();
    } else {
      setNavigationGuardModalIsOpen(true);
    }
  };

  if (innerComponentApiError !== null) {
    return <RequestFailedPage error={innerComponentApiError} />;
  }

  return (
    <>
      <PaddedPage>
        <HeaderContainer>
          <Header1>{translate('pages.createDashboard.header')}</Header1>
          <ActionButtonsContainer>
            <MinimalButton icon={<TimesSolidIcon />} buttonStyle="black" onClick={onCancelClick}>
              {translate('actionButtons.cancel')}
            </MinimalButton>
            <MinimalButton icon={<FloppyDiskIcon />} onClick={onSaveClick}>
              {translate('pages.createDashboard.saveDashboard')}
            </MinimalButton>
          </ActionButtonsContainer>
        </HeaderContainer>
        <CreateEditDashboardForm
          onSubmit={onFormSubmit}
          initialValues={initialFormModel}
          onInnerComponentApiError={setInnerComponentApiError}
          error={createDashboardRequest.state.error}
          ref={formRef}
        />
      </PaddedPage>
      <ActionAlert
        alertType="negative"
        isOpen={navigationGuardModalIsOpen}
        onRequestClose={closeNavigationGuardModal}
        title={translate('pages.createDashboard.navigationGuardModal.title')}
        message={translate('pages.createDashboard.navigationGuardModal.message')}
        cancelButtonText={translate('actionButtons.cancel')}
        confirmButtonText={translate('pages.createDashboard.navigationGuardModal.confirmText')}
        onInitiateAction={returnToDashboards}
        actionInProgress={false}
        actionError={null}
      />
    </>
  );
};

type CreateDashboardCommand = BaseCreateEditDashboardCommand;

const initialFormModel: CreateEditDashboardFormModel = {
  name: '',
  loginRole: roleCodeCharactersByRoleName.Manager,
  accessGroupId: null,
  components: [],
};

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: ${spacing16};
`;
